// Copyright (c) 2026 GLOBAL MICRO IT TECHNOLOGY SOLUTIONS LIMITED
// Company No. 758762, Dublin, Ireland
// All rights reserved. Proprietary and confidential.

// Shared chat-shell component — owned by gms/M365-Operations-Agent, fanned byte-identical to
// Audit + Cloud-Adoption via .claude/shared-frontend.json + sync-check.yml. Do not add
// per-agent logic here; pass it through props (see the `variant` prop). See Workstream B.

import React, { memo, isValidElement } from 'react';
import ReactMarkdown, { type Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeSanitize from 'rehype-sanitize';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vs } from 'react-syntax-highlighter/dist/esm/styles/prism';
import './MarkdownMessage.css';

// ── Report status detection helpers (#861, #864) ──

/** Recursively extract plain text from a React node tree. */
function extractText(node: unknown): string {
  if (typeof node === 'string') return node;
  if (typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(extractText).join('');
  if (isValidElement(node)) {
    return extractText((node.props as Record<string, unknown>)?.children);
  }
  return '';
}

/** Detect emoji-prefixed status indicators and score-heat thresholds in table cells. */
function detectCellClass(text: string): string | null {
  const t = text.trim();
  // Status indicators (emoji-prefixed from report builders)
  if (t.startsWith('🟢')) return 'md-status-pass';
  if (t.startsWith('🟡') || t.startsWith('🟠')) return 'md-status-attention';
  if (t.startsWith('🔴')) return 'md-status-fail';
  if (t.startsWith('⚪')) return 'md-status-manual';
  if (t.startsWith('🔵')) return 'md-status-info';
  // Trend direction indicators
  if (t.startsWith('↑')) return 'md-trend-up';
  if (t.startsWith('↓')) return 'md-trend-down';
  if (t.startsWith('→ ')) return 'md-trend-flat';
  // Score heat — percentage-only cells (e.g. "74.3%" but not "74.3% of users").
  // Flattened alternation (not `\d+(?:\.\d+)?`) keeps regex star-height at 1 for
  // eslint-plugin-security/detect-unsafe-regex while matching the same inputs.
  const match = /^(\d+|\d+\.\d+)%$/.exec(t);
  if (match) {
    const value = parseFloat(match[1]);
    if (value >= 80) return 'md-score-good';
    if (value >= 50) return 'md-score-attention';
    return 'md-score-critical';
  }
  return null;
}

/**
 * Markdown rendering richness.
 * - `full` — the rich chat renderer: Prism syntax highlighting, Unicode bar-charts, and
 *   emoji status-cell / score-heat / trend colouring (the canonical chat rendering).
 * - `minimal` — plain markdown with safe links; no highlighting or report-cell detection.
 * - `inline` — `minimal`, collapsed to a single inline flow for compact previews.
 */
export type MarkdownVariant = 'full' | 'minimal' | 'inline';

interface MarkdownMessageProps {
  content: string;
  variant?: MarkdownVariant;
}

/** Links — always open in a new tab (shared by every variant). */
const renderLink: Components['a'] = ({ href, children }) => (
  <a href={href} target="_blank" rel="noopener noreferrer" className="markdown-link">
    {children}
  </a>
);

/** Rich (`full`) component overrides: highlighted code, bar-charts, status cells. */
const fullComponents: Components = {
  code({ className, children, ...props }) {
    const match = /language-(\w+)/.exec(className ?? '');
    const raw = Array.isArray(children) ? children.join('') : (children as string);
    const codeString = raw.replace(/\n$/, '');

    if (match) {
      return (
        <SyntaxHighlighter
          style={vs}
          language={match[1]}
          PreTag="div"
          customStyle={{
            borderRadius: '4px',
            fontSize: '13px',
          }}
        >
          {codeString}
        </SyntaxHighlighter>
      );
    }

    // Bar chart detection: inline code containing only Unicode block characters
    if (/^[█░]+$/.test(codeString)) {
      const filled = (codeString.match(/█/g) || []).length;
      const total = codeString.length;
      const pct = total > 0 ? Math.round(filled / total * 100) : 0;
      return <span className="md-bar" style={{ '--md-bar-pct': `${pct}%` } as React.CSSProperties} />;
    }

    return (
      <code className="inline-code" {...props}>
        {children}
      </code>
    );
  },
  a: renderLink,
  td({ children, style }) {
    const text = extractText(children);
    const cls = detectCellClass(text);
    return cls
      ? <td style={style}><span className={cls}>{children}</span></td>
      : <td style={style}>{children}</td>;
  },
};

/** Lean (`minimal` / `inline`) component overrides: safe links only. */
const leanComponents: Components = {
  a: renderLink,
};

export const MarkdownMessage = memo(function MarkdownMessage({
  content,
  variant = 'full',
}: MarkdownMessageProps) {
  const className =
    variant === 'full' ? 'markdown-message' : `markdown-message markdown-message--${variant}`;
  return (
    <div className={className}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeSanitize]}
        components={variant === 'full' ? fullComponents : leanComponents}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
});
